import { z } from "zod";
import { DataFormatSchema, DataConditionSchema } from "./phase";
import { WorkflowWithPhasesSchema } from "./workflow";
import { ClassificationSchema } from "./publication";
// ============================================================================
// AI CHAT SCHEMAS
// ============================================================================

export const ChatMessageSchema = z.object({
  role: z.enum(["user", "assistant", "system"]),
  content: z.string(),
});

// Agent data for AI chat context (only essential fields for AI suggestions)
// Combines basic info + classification fields (specialty, usageContext, operations, patientTypes)
export const AgentChatDataSchema = z
  .object({
    id: z.string().optional(),
    name: z.string(),
    description: z.string().nullable().optional(),
  })
  .merge(ClassificationSchema);

// Schema for workflow update summary (passed after validation)
export const WorkflowUpdateSummarySchema = z.object({
  workflowNameUpdated: z.boolean(),
  workflowDescriptionUpdated: z.boolean(),
  phasesCreated: z.number(),
  phasesUpdated: z.number(),
  totalPhases: z.number(),
  isCompleteWorkflow: z.boolean(),
  phaseNames: z.array(z.string()),
});

export const WorkflowBuilderChatBodySchema = z.object({
  messages: z.array(ChatMessageSchema),
  workflowData: WorkflowWithPhasesSchema.optional(),
  agentData: AgentChatDataSchema.optional(),
  userValidated: z.boolean().optional(),
  updateSummary: WorkflowUpdateSummarySchema.optional(),
});

// ============================================================================
// AI WORKFLOW GENERATION SCHEMAS
// ============================================================================

// Schema for generating workflow from simple description (initial creation)
export const GenerateWorkflowBodySchema = z.object({
  description: z.string().min(1, "Workflow description is required"),
  specialty: z.string().optional(),
  patientTypes: z.array(z.string()).optional(),
  additionalContext: z.string().optional(),
});

// Schema for generating workflow JSON from chat conversation (after validation)
export const GenerateWorkflowFromChatSchema = z.object({
  messages: z.array(ChatMessageSchema),
  workflowData: WorkflowWithPhasesSchema.optional(),
});

// Schema for workflow generation response (partial workflow data from AI)
export const WorkflowGenerationResponseSchema = z.object({
  workflow: z
    .object({
      name: z.string().optional(),
      description: z.string().optional(),
      phases: z.array(z.any()).optional(), // Phases with only AI-generated fields
    })
    .nullable(),
  truncated: z.boolean().optional(), // Indicates if generation was truncated due to token limit
  requiresPhaseByPhase: z.boolean().optional(), // Indicates protocol is too complex, needs phase-by-phase generation
  message: z.string().optional(), // User-facing message if truncated or requires phase-by-phase
});

// ============================================================================
// AI QUESTIONNAIRE EXTRACTION SCHEMAS
// ============================================================================

export const ExtractQuestionnaireBodySchema = z.object({
  text: z.string().min(1, "Text is required"),
  context: z.string().optional(),
});

// ============================================================================
// AI QUESTION SUGGESTION SCHEMAS
// ============================================================================

export const phaseContextSchema = z.object({
  name: z.string().min(1, "Phase name is required"),
  description: z.string().optional(),
  frequency: z.string().optional(),
});

export const SuggestQuestionBodySchema = z.object({
  phaseContext: phaseContextSchema,
  existingQuestions: z.array(z.string()).optional(),
  specialty: z.string().optional(),
  dataFormat: DataFormatSchema.optional(),
  dataConditions: z.array(DataConditionSchema).optional(),
  dataName: z.string().min(1, "Data name is required"),
});

// ============================================================================
// AI PHASE DELETION NOTIFICATION SCHEMAS
// ============================================================================

export const NotifyPhaseDeletionBodySchema = z.object({
  deletedPhaseIndex: z.number().int().min(0),
  updatedWorkflow: z.object({
    id: z.string().optional(),
    name: z.string(),
    description: z.string().optional(),
    phases: z.array(z.unknown()).optional(), // Array of phases (any structure)
  }),
});

// ============================================================================
// TYPE EXPORTS
// ============================================================================

export type ChatMessage = z.infer<typeof ChatMessageSchema>;
export type AgentChatData = z.infer<typeof AgentChatDataSchema>;
export type WorkflowUpdateSummary = z.infer<typeof WorkflowUpdateSummarySchema>;
export type WorkflowBuilderChatBody = z.infer<typeof WorkflowBuilderChatBodySchema>;
export type GenerateWorkflowBody = z.infer<typeof GenerateWorkflowBodySchema>;
export type GenerateWorkflowFromChat = z.infer<typeof GenerateWorkflowFromChatSchema>;
export type WorkflowGenerationResponse = z.infer<typeof WorkflowGenerationResponseSchema>;
export type ExtractQuestionnaireBody = z.infer<typeof ExtractQuestionnaireBodySchema>;
export type SuggestQuestionBody = z.infer<typeof SuggestQuestionBodySchema>;
export type NotifyPhaseDeletionBody = z.infer<typeof NotifyPhaseDeletionBodySchema>;
export type PhaseContext = z.infer<typeof phaseContextSchema>;
